import { css } from '@/styled-system/css';

// Main container - flex column layout
export const gridContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  paddingTop: '24px',
  paddingBottom: '24px',
  paddingLeft: '8px',
  paddingRight: '8px',
  position: 'relative',
  cursor: 'pointer',
  backgroundColor: '{colors.hpxWhite}',
  md: {
    paddingLeft: '16px',
    paddingRight: '16px',
  },
});

// Row 1: Photo + Description + Contact Button (3-column structure)
export const headerRowStyles = css({
  display: 'grid',
  gridTemplateColumns: '64px 1fr auto', // Mobile: fixed photo width | flexible description | contact
  gap: '16px',
  alignItems: 'flex-start',
  marginBottom: '2x',
  md: {
    gridTemplateColumns: '80px 1fr auto', // Tablet/Desktop: optimized photo width
  },
  xxl: {
    gridTemplateColumns: '80px 1fr auto', // Large Desktop: optimized photo width
  },
});

// Photo area
export const photoAreaStyles = css({
  position: 'relative',
  width: '60px',
  height: '60px',
  minWidth: '60px',
  minHeight: '60px',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: '{colors.hpxGrey100}',
  marginTop: '0',
  marginRight: 'auto',
  marginBottom: '0',
  marginLeft: 'auto',
  md: {
    width: '80px',
    height: '80px',
    minWidth: '80px',
    minHeight: '80px',
    marginTop: '0',
    marginRight: 'auto',
    marginBottom: '0',
    marginLeft: 'auto',
  },
});

// Description area (beds, baths, sqft) - aligns with price column
export const descriptionAreaStyles = css({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
});

// Contact button area (3rd column in 3-column grid)
export const contactAreaStyles = css({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'flex-end',
});

// Data row (Row 3+): Badge + Price + Availability + Unit - No borders
export const dataRowStyles = css({
  display: 'grid',
  gridTemplateColumns: '64px 3fr 2fr 1fr', // Mobile: fixed badge width
  gap: '16px',
  alignItems: 'flex-start',
  marginBottom: '1x',
  position: 'relative',
  md: {
    gridTemplateColumns: '80px 2fr 2fr 1fr', // Tablet: optimized badge width
  },
  xxl: {
    gridTemplateColumns: '80px 2fr 2fr 1fr', // Large Desktop: optimized badge width
  },
});

// 3D Tour button area (first column of header row)
export const tourOrBadgeAreaStyles = css({
  display: 'flex',
  justifyContent: 'center', // Center-align 3D tour button
  alignItems: 'center',
  width: '64px',
  height: '32px',
  md: {
    width: '80px',
    height: '32px',
  },
  xxl: {
    width: '80px',
    justifyContent: 'center', // Keep center-aligned for XXL
  },
});

// Badge area (first column of data rows) - baseline-aligned with vertical centering offset
export const badgeAreaStyles = css({
  display: 'flex',
  justifyContent: 'center', // Center-align badges
  alignItems: 'baseline',
  width: '64px',
  height: '32px',
  marginTop: '3px', // Offset badge down to center it with text
  md: {
    width: '80px',
    height: '32px',
    justifyContent: 'center', // Center-align badges for md
  },
  xxl: {
    width: '80px',
    justifyContent: 'center', // Keep center-aligned for XXL
  },
});

// Header row (Row 2): 3D Tour + Column Headers with selective underline
export const headerLabelsRowStyles = css({
  display: 'grid',
  gridTemplateColumns: '64px 3fr 2fr 1fr', // Mobile: fixed tour width
  gap: '16px',
  alignItems: 'center',
  marginBottom: '1x',
  color: '{colors.hpxGrey600}',
  fontSize: 'sm',
  position: 'relative',
  paddingBottom: '1x',
  md: {
    gridTemplateColumns: '80px 2fr 2fr 1fr', // Tablet/Desktop: optimized tour width
  },
  xxl: {
    gridTemplateColumns: '80px 2fr 2fr 1fr', // Large Desktop: optimized tour width
  },
  _after: {
    content: '""',
    position: 'absolute',
    bottom: '0',
    left: '80px', // Mobile: Start after 3D tour area (64px) + gap (16px)
    right: '0',
    height: '1px',
    backgroundColor: '{colors.hpxGrey100}',
    md: {
      left: '96px', // md+: Start after 3D tour area (80px) + gap (16px)
    },
    xxl: {
      left: '96px', // xxl+: Start after 3D tour area (80px) + gap (16px)
    },
  },
});

// Unit labels header
export const unitLabelsStyles = css({
  display: 'flex',
  color: '{colors.hpxGrey600}',
  fontSize: 'sm',
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid',
  borderBottomColor: '{colors.hpxGrey100}',
  marginBottom: '1x',
});

// Individual unit row
export const unitRowStyles = css({
  display: 'flex',
  paddingBottom: '2x',
  _last: {
    paddingBottom: '0',
  },
});

// Unit price column
export const unitPriceStyles = css({
  flex: '3',
  textAlign: 'left',
  fontWeight: 'bold',
  md: {
    flex: '2',
  },
});

// Unit price header (same layout as unitPriceStyles but regular weight)
export const unitPriceHeaderStyles = css({
  flex: '3',
  textAlign: 'left',
  fontWeight: 'normal',
  md: {
    flex: '2',
  },
});

// Unit availability column
export const unitAvailabilityStyles = css({
  flex: '2',
});

// Unit name column
export const unitNameStyles = css({
  flex: '1',
});

// Detail text styles
export const detailStyles = css({
  paddingRight: '1x',
  xl: {
    paddingRight: '2x',
  },
});

export const boldDetailStyles = css({
  fontWeight: '700',
  paddingRight: '1x',
  xl: {
    paddingRight: '2x',
  },
});

// Bath/sqft styles
export const bathSqftStyles = css({
  paddingRight: '1x',
  whiteSpace: 'nowrap',
  float: 'left',
});

// Contact button styles
export const contactButtonStyles = css({
  display: 'flex',
  alignItems: 'center',
});

export const contactIconStyles = css({
  width: '16px',
  height: '16px',
  maxWidth: 'none',
  marginRight: '6px',
});

// Expand section
export const expandStyles = css({
  paddingTop: '2x',
});
